import React from 'react';
import { useHistory } from 'react-router-dom';

import { isBefore, isAfter, isEqual, format, add } from 'date-fns';

import { Flex } from '@lucidhq/lucidium';
import { Field, Form } from '@lucidhq/lucidium/components/FormV2';

import { usePermissions } from '~/components/Permissions/PermissionsProvider';
import { FormProps } from '~/components/Shared/FormProps';
import { useMutateTrackingOnlyCampaign } from '~/components/Shared/hooks/useMutateTrackingOnlyCampaign';
import { ROUTES } from '~/constants';
import { Campaign, CampaignTypes, TrackingOnlyCampaign } from '~/types';
import { getCampaignManagerRoute } from '~/utils/campaignManager';
import { t } from '~/utils/i18n';

import { AccountField } from '../CampaignDetailsStep/CampaignDetailsForm/AccountField';
import { CountryLanguageField } from '../CampaignDetailsStep/CampaignDetailsForm/CountryLanguageField';
import { EndDateField } from '../CampaignDetailsStep/CampaignDetailsForm/EndDateField';
import { StartDateField } from '../CampaignDetailsStep/CampaignDetailsForm/StartDateField';
import { CampaignManagerFooter } from '../CampaignManagerFooter';
import { useCampaignManager } from '../CampaignManagerProvider';

const initialValues = {
  campaign_name: '',
  country_language_id: 1,
  end_date: '',
  start_date: '',
  account_id: undefined,
  permission_id: 0
};

const handleValidate = (values: Campaign, initialValues?: Campaign) => {
  const errors: Record<string, any> = {};
  const draft_settings: Record<string, any> = {};

  if (!values.campaign_name) {
    errors.campaign_name = t('forms.errors.requiredField');
  }

  if (!values.start_date) {
    errors.start_date = t('forms.errors.requiredField');
  }

  if (!values.end_date) {
    errors.end_date = t('forms.errors.requiredField');
  }

  if (!!values?.start_date && !!values?.end_date) {
    // This adjustment is mainly to check only the day value and validate the difference between dates.
    const adjustedStartDate = add(new Date(values?.start_date), { days: 1 });
    const startDate = new Date(format(adjustedStartDate, 'MM/dd/yyyy'));
    const endDate = new Date(format(new Date(values.end_date), 'MM/dd/yyyy'));

    if (isAfter(startDate, endDate)) {
      errors.start_date = t('forms.errors.startDate');
    }

    if (isBefore(endDate, startDate)) {
      errors.end_date = t('forms.errors.endDate');
    }

    if (isEqual(startDate, endDate)) {
      errors.start_date = t('forms.errors.startDate');
      errors.end_date = t('forms.errors.endDate');
    }

    if (isBefore(endDate, new Date())) {
      errors.end_date = t('forms.errors.endDatePast');
    }

    if (initialValues?.end_date) {
      const initialEndDate = new Date(
        format(new Date(initialValues.end_date), 'MM/dd/yyyy')
      );

      if (isBefore(endDate, initialEndDate)) {
        errors.end_date = t('forms.errors.endDateLive');
      }
    }
  }

  return {
    ...errors,
    ...(Object.keys(draft_settings).length && { draft_settings })
  };
};

export const ConnectCampaignDetailsForm = () => {
  const { isSuperAdmin } = usePermissions();
  const history = useHistory();
  const {
    goToStep,
    steps,
    activeStep,
    managedCampaign,
    refetchCampaign,
    onMutateError,
    can
  } = useCampaignManager();

  const defaultValues = !!managedCampaign
    ? { ...managedCampaign.details }
    : initialValues;
  const initValues = defaultValues as Campaign;

  const { mutate } = useMutateTrackingOnlyCampaign(
    managedCampaign?.details?.campaign_id
  );

  const hasCampaign = managedCampaign?.details?.campaign_id;

  const handleBack = () => history.push(ROUTES.adminCampaigns);

  const handleSubmit = (action: 'close' | 'continue') => (
    values: TrackingOnlyCampaign
  ) => {
    const formattedValues = !!hasCampaign
      ? values
      : {
          ...values,
          //@NOTE: this mapping is needed due to the reuse of the account field and the response that we receive after the study is created/updated.
          permission_group_id: values?.permission_id ?? 0
        };

    if (formattedValues.hasOwnProperty('permission_id')) {
      delete formattedValues?.permission_id;
    }

    mutate(formattedValues, {
      onSuccess: data => {
        if (!hasCampaign) {
          if (action === 'close') return handleBack();

          if (action === 'continue') {
            history.push(
              getCampaignManagerRoute(
                CampaignTypes.TRACKING_ONLY,
                data.campaign_id,
                steps.MEDIA_TRACKING.id
              )
            );
          }
        } else {
          if (action === 'close') return handleBack();

          if (action === 'continue') {
            refetchCampaign();
            const nextStep = can.limitedEdit
              ? steps.REVIEW.id
              : steps[activeStep].nextId;
            if (nextStep) goToStep(nextStep);
          }
        }
      },
      ...onMutateError
    });
  };

  return (
    <Form
      initialValues={initValues}
      validate={values =>
        handleValidate(values, can.limitedEdit ? initValues : undefined)
      }
      // @ts-ignore
      onSubmit={() => null}
      validateOnBlur={false}
      validateOnChange={false}
      enableReinitialize
    >
      <Field
        required
        name={'campaign_name'}
        label={t(`forms.labels.studyName`)}
        readOnly={!!hasCampaign && !(can.edit || can.limitedEdit)}
      />

      <Flex lss={{ justifyContent: 'space-between', mt: 'md' }}>
        <StartDateField
          name={'start_date'}
          readOnly={!!hasCampaign && !can.edit}
        />
        <EndDateField name={'end_date'} readOnly={!!hasCampaign && !can.edit} />
      </Flex>
      <Flex lss={{ flexDirection: 'column', mt: 'xl' }}>
        {isSuperAdmin && (
          <AccountField
            readOnly={!!hasCampaign && !can.edit}
            isRequired={false}
          />
        )}
      </Flex>

      <CountryLanguageField
        name={'country_language_id'}
        readOnly={!!hasCampaign && !can.edit}
      />

      <FormProps>
        {({ validateForm, values }) => (
          <CampaignManagerFooter
            onBack={handleBack}
            saveCloseProps={{
              onClick: async () => {
                const errors = await validateForm();

                if (Object.values(errors).length) return;

                const formHandler = handleSubmit('close');

                formHandler(values as Campaign);
              },
              type: 'button'
            }}
            saveContinueProps={{
              onClick: async () => {
                const errors = await validateForm();

                if (Object.values(errors).length) return;

                const formHandler = handleSubmit('continue');

                formHandler(values as Campaign);
              },
              type: 'submit'
            }}
          />
        )}
      </FormProps>
    </Form>
  );
};
